import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@interacta-shared/data-access-configuration';
import {
    ResourceIdentifier,
    TemporaryLinkKind,
} from '@interacta-shared/data-access-storage-temporary-link';
import { map, Observable, throwError } from 'rxjs';

const paramMap: Record<TemporaryLinkKind, Record<string, boolean>> = {
    temporaryContentViewLink: { loadViewLink: true },
    temporaryContentDownloadLink: { loadDownloadLink: true },
    temporaryContentPreviewImageLink: { loadPreviewImageLink: true },
    temporaryContentPreviewImageHiResLink: { loadPreviewImageHiResLink: true },
    temporaryContentPreviewImageHiResAnimatedLink: {
        loadPreviewImageHiResAnimatedLink: true,
    },
};

/**
 * Shell-specific service for generating temporary storage links.
 *
 * This service provides the concrete implementation for generating new temporary links
 * in the shell application context. It integrates with the backend API to request
 * new temporary URLs for storage resources.
 *
 * @example
 * ```typescript
 * const resourceId: ResourceIdentifier = {
 *   entityId: 123,
 *   entityType: 'post',
 *   contentRef: 'abc123def',
 *   temporaryLinkKind: 'temporaryContentViewLink'
 * };
 *
 * this.shellService.getNewTemporaryLink(resourceId)
 *   .subscribe(newUrl => console.log('New temporary URL:', newUrl));
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class ShellTemporaryLinkService {
    private readonly http = inject(HttpClient);

    private readonly baseUrlSettings = `${inject(ENVIRONMENT).apiBasePath.common}/internal/v2/communication/attachments/data`;

    /**
     * Generates a new temporary link for the specified resource.
     *
     * This method will make an HTTP request to the backend API to generate
     * a new temporary URL for the given resource identifier.
     *
     * @param resourceId - The resource identifier containing entity and content information
     * @returns Observable that emits the new temporary link URL
     * @throws Error when the API implementation is not yet available
     *
     * @todo Implement actual API call to backend service
     * @todo Add proper error handling and retry logic
     * @todo Add request/response type definitions
     */
    getNewTemporaryLink(resourceId: ResourceIdentifier): Observable<string> {
        // TODO IISP-12055
        if (resourceId.entityType !== 'post') {
            return throwError(
                () =>
                    new Error(
                        `Unsupported entity type ${resourceId.entityType}`,
                    ),
            );
        }
        if (resourceId.entityId === null) {
            return throwError(() => new Error('Unsupported entity id null'));
        }
        return this.http
            .post<Record<string, string>>(
                `${this.baseUrlSettings}/signed-urls/regenerate`,
                {
                    contentRef: resourceId.contentRef,
                    postId: resourceId.entityId,
                },
                { params: paramMap[resourceId.temporaryLinkKind] },
            )
            .pipe(map((result) => result[resourceId.temporaryLinkKind]));
    }
}
